<template>
  <Teleport to="body">
    <Transition name="popup-fade">
      <div 
        v-if="isOpen" 
        class="field-popup-overlay"
        @click.self="closePopup"
      >
        <div class="field-popup">
          <!-- Header -->
          <div class="popup-header">
            <h3 class="popup-title">{{ fieldData?.field_name || $t('Field Information') }}</h3>
            <button @click="closePopup" class="popup-close-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="popup-content">
            <!-- Basic Info -->
            <div v-if="fieldData" class="info-section">
              <!-- Field Name with ID inline -->
              <div class="info-row" v-if="fieldData.name">
                <span class="info-label">{{$t('Field')}}:</span>
                <span class="info-value">
                  {{ fieldData.name }}
                  <span v-if="fieldData.key" class="field-id">#{{ fieldData.key }}</span>
                </span>
              </div>

              <!-- Crop Type with color badge -->
              <div class="info-row" v-if="fieldData.crop_type">
                <span class="info-label">{{$t('Crop')}}:</span>
                <span class="info-value">
                  <span 
                    class="crop-badge" 
                    :style="getCropBadgeStyle(fieldData.color_code)"
                  >
                    {{ fieldData.crop_type }}
                  </span>
                </span>
              </div>

              <!-- Area -->
              <div class="info-row" v-if="fieldData.area">
                <span class="info-label">{{$t('Area')}}:</span>
                <span class="info-value">{{ formatArea(fieldData.area) }}</span>
              </div>

              <!-- Determination Info - compact inline -->
              <div class="info-row compact" v-if="fieldData.determination_datetime">
                <span class="info-label">{{$t('Updated')}}:</span>
                <span class="info-value text-grey-6">{{ formatDate(fieldData.determination_datetime) }}</span>
              </div>

              <div class="info-row compact" v-if="fieldData.determination_method">
                <span class="info-label">{{$t('Method')}}:</span>
                <span class="info-value">
                  <span class="method-badge">
                    {{ formatMethod(fieldData.determination_method) }}
                  </span>
                </span>
              </div>

              <!-- Satellite Data - compact grid -->
              <div v-if="hasSatelliteData" class="satellite-grid">
                <div class="satellite-item" v-if="fieldData['planet:ca_ratio']">
                  <span class="metric-label">{{$t('CA Ratio')}}</span>
                  <span class="metric-value">{{ formatNumber(fieldData['planet:ca_ratio']) }}</span>
                </div>

                <div class="satellite-item" v-if="fieldData['planet:micd']">
                  <span class="metric-label">MICD</span>
                  <span class="metric-value">{{ formatNumber(fieldData['planet:micd']) }}</span>
                </div>

                <div class="satellite-item" v-if="fieldData['planet:qa'] !== undefined">
                  <span class="metric-label">{{$t('Quality')}}</span>
                  <span class="qa-badge" :class="getQualityClass(fieldData['planet:qa'])">
                    {{ fieldData['planet:qa'] }}
                  </span>
                </div>
              </div>

              <!-- Additional metadata (any other fields) -->
              <div v-if="hasAdditionalData" class="info-divider"></div>
              
              <div 
                v-for="(value, key) in additionalData" 
                :key="key"
                class="info-row compact"
              >
                <span class="info-label">{{ formatLabel(key) }}:</span>
                <span class="info-value text-grey-6">{{ formatValue(value) }}</span>
              </div>
            </div>

            <!-- No data fallback -->
            <div v-else class="no-data">
              <p class="text-grey-5">{{$t('No field information available')}}</p>
            </div>
          </div>

          <!-- Actions -->
          <div class="popup-actions">
            <button @click="setAsHome" class="action-btn primary">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="margin-right: 4px;">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" stroke-width="2"/>
                <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" stroke-width="2"/>
              </svg>
              {{$t('Set as Home')}}
            </button>
            <button @click="closePopup" class="action-btn">
              {{$t('Close')}}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: 'FieldInfoPopup',
  
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    fieldData: {
      type: Object,
      default: null
    },
    coordinate: {
      type: Array,
      default: null
    }
  },

  emits: ['close', 'set-as-home'],

  computed: {
    hasSatelliteData() {
      if (!this.fieldData) return false
      return this.fieldData['planet:ca_ratio'] !== undefined || 
             this.fieldData['planet:micd'] !== undefined ||
             this.fieldData['planet:qa'] !== undefined
    },

    additionalData() {
      if (!this.fieldData) return {}
      
      const standardFields = [
        'name', 'crop_type', 'area', 'perimeter', 
        'color_code', 'fill_color_code', 'geometry', 'geometry_type_id',
        'key', 'id', 'field_id',
        'determination_datetime', 'determination_method',
        'planet:ca_ratio', 'planet:micd', 'planet:qa'
      ]
      
      return Object.keys(this.fieldData)
        .filter(key => !standardFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = this.fieldData[key]
          return obj
        }, {})
    },

    hasAdditionalData() {
      return Object.keys(this.additionalData).length > 0
    }
  },

  methods: {
    closePopup() {
      this.$emit('close')
    },

    setAsHome() {
      this.$emit('set-as-home', {
        fieldData: this.fieldData,
        coordinate: this.coordinate
      })
    },

    formatArea(area) {
      if (!area) return 'N/A'
      const hectares = parseFloat(area)
      return `${hectares.toFixed(2)} ha`
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      } catch (e) {
        return dateString
      }
    },

    formatMethod(method) {
      if (!method) return 'N/A'
      return method
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    formatNumber(value) {
      if (value === null || value === undefined) return 'N/A'
      const num = parseFloat(value)
      return num.toFixed(2)
    },

    getQualityClass(qa) {
      if (qa === 0) return 'quality-good'
      if (qa <= 2) return 'quality-medium'
      return 'quality-low'
    },

    formatLabel(key) {
      return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    formatValue(value) {
      if (value === null || value === undefined) return 'N/A'
      if (typeof value === 'boolean') return value ? 'Yes' : 'No'
      if (typeof value === 'number') return value.toLocaleString()
      return value.toString()
    },

    getCropBadgeStyle(colorCode) {
      const bgColor = colorCode || '#4CAF50'
      const textColor = this.getContrastColor(bgColor)
      return {
        backgroundColor: bgColor,
        color: textColor
      }
    },

    getContrastColor(hexColor) {
      const hex = hexColor.replace('#', '')
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.6 ? '#000000' : '#FFFFFF'
    }  
  }
}
</script>

<style lang="scss" scoped>
.field-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.field-popup {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  body.body--dark & {
    background: $dark;
  }
}

/* Header */
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;

  body.body--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }
}

.popup-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--q-color-grey-10, #1f2937);
  margin: 0;

  body.body--dark & {
    color: white;
  }
}

.popup-close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: var(--q-color-grey-2, #f3f4f6);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--q-color-grey-6, #6b7280);
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: var(--q-color-grey-8, #374151);

    body.body--dark & {
      background: rgba(255, 255, 255, 0.04);
    }
  }

  body.body--dark & {
    background: rgba(255, 255, 255, 0.05);
    color: var(--q-color-grey-4);
  }
}

/* Content */
.popup-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  min-height: 28px;

  &.compact {
    min-height: 24px;
    gap: 8px;
  }
}

.info-label {
  font-weight: 500;
  color: var(--q-color-grey-6, #6b7280);
  font-size: 0.8125rem;
  min-width: 80px;

  body.body--dark & {
    color: var(--q-color-grey-5);
  }
}

.info-value {
  color: var(--q-color-grey-8, #1f2937);
  font-size: 0.8125rem;
  text-align: right;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;

  body.body--dark & {
    color: white;
  }

  &.text-grey-6 {
    color: var(--q-color-grey-6, #6b7280);

    body.body--dark & {
      color: var(--q-color-grey-5);
    }
  }
}

.field-id {
  color: var(--q-color-grey-5, #9ca3af);
  font-size: 0.6875rem;
  font-weight: 500;

  body.body--dark & {
    color: var(--q-color-grey-6);
  }
}

.crop-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 10px;
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.method-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--q-color-blue-2, #e0f2fe);
  color: var(--q-color-blue-9, #0369a1);
  font-size: 0.6875rem;
  font-weight: 500;
}

/* Satellite data grid - compact */
.satellite-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;

  body.body--dark & {
    border-top-color: rgba(255, 255, 255, 0.12);
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }
}

.satellite-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px;
  background: var(--q-color-grey-1, #f9fafb);
  border-radius: 6px;

  body.body--dark & {
    background: rgba(255, 255, 255, 0.05);
  }
}

.metric-label {
  font-size: 0.625rem;
  color: var(--q-color-grey-6, #9ca3af);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;

  body.body--dark & {
    color: var(--q-color-grey-5);
  }
}

.metric-value {
  font-size: 0.8125rem;
  color: var(--q-color-grey-10, #1f2937);
  font-weight: 600;

  body.body--dark & {
    color: white;
  }
}

.qa-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;

  &.quality-good {
    background: var(--q-color-green-2, #dcfce7);
    color: var(--q-color-green-9, #15803d);
  }

  &.quality-medium {
    background: var(--q-color-amber-2, #fef3c7);
    color: var(--q-color-amber-9, #b45309);
  }

  &.quality-low {
    background: var(--q-color-red-2, #fee2e2);
    color: var(--q-color-red-9, #b91c1c);
  }
}

.info-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 8px 0;

  body.body--dark & {
    background: rgba(255, 255, 255, 0.12);
  }
}

.no-data {
  text-align: center;
  padding: 40px 20px;

  p {
    margin: 0;
  }

  .text-grey-5 {
    color: var(--q-color-grey-5, #9ca3af);

    body.body--dark & {
      color: var(--q-color-grey-6);
    }
  }
}

/* Actions */
.popup-actions {
  display: flex;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;

  body.body--dark & {
    border-top-color: rgba(255, 255, 255, 0.12);
  }
}

.action-btn {
  flex: 1;
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  background: white;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--q-color-grey-8, #374151);

  &:hover {
    background: rgba(0, 0, 0, 0.04);

    body.body--dark & {
      background: rgba(255, 255, 255, 0.04);
    }
  }

  &.primary {
    background: var(--q-color-primary, #1976d2);
    color: white;
    border-color: var(--q-color-primary, #1976d2);
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: var(--q-color-primary-dark, #1565c0);
      border-color: var(--q-color-primary-dark, #1565c0);
    }
  }

  body.body--dark & {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
    color: white;
  }
}

/* Animations */
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.3s ease;
}

.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}

.popup-fade-enter-active .field-popup,
.popup-fade-leave-active .field-popup {
  transition: transform 0.3s ease;
}

.popup-fade-enter-from .field-popup {
  transform: scale(0.9) translateY(20px);
}

.popup-fade-leave-to .field-popup {
  transform: scale(0.9) translateY(20px);
}

/* Responsive */
@media (max-width: 640px) {
  .field-popup {
    max-width: 100%;
    max-height: 90vh;
    margin: 0;
  }

  .satellite-grid {
    gap: 6px;
  }

  .satellite-item {
    padding: 4px;
  }

  .metric-label {
    font-size: 0.5625rem;
  }

  .metric-value {
    font-size: 0.75rem;
  }
}
</style>